import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { KenteStripe, Button } from "./Primitives";
import { cn } from "../lib/utils";

const links = [
  { to: "/kingdoms", label: "Kingdoms" },
  { to: "/timeline", label: "Timeline" },
  { to: "/stories", label: "Stories" },
  { to: "/dispersion", label: "Dispersion" },
  { to: "/globe", label: "Globe" },
  { to: "/lessons", label: "Lessons" },
  { to: "/quizzes", label: "Quizzes" },
  { to: "/assignments", label: "Assignments" },
  { to: "/resources", label: "Library" },
  { to: "/tutor", label: "AI Tutor" },
];

export default function Navbar() {
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 border-b border-edge bg-ink/90 backdrop-blur-md" data-testid="navbar">
      <KenteStripe />
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-4 md:px-12">
        <Link to="/" className="flex items-baseline gap-2" data-testid="brand-link">
          <span className="whitespace-nowrap font-serif text-xl font-semibold tracking-tight text-bone sm:text-2xl">Our African Narratives</span>
        </Link>

        <div className="hidden items-center gap-4 lg:flex xl:gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                cn(
                  "whitespace-nowrap text-sm tracking-wide transition-colors hover:text-gold",
                  isActive ? "text-gold" : "text-bone"
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          {user?.is_premium ? (
            <span className="whitespace-nowrap border border-gold/50 bg-gold/10 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-gold" data-testid="premium-badge">
              Premium
            </span>
          ) : (
            <button
              onClick={() => navigate("/premium")}
              data-testid="nav-premium-btn"
              className="whitespace-nowrap border border-gold bg-gold/10 px-3 py-1.5 text-sm text-gold transition-colors hover:bg-gold hover:text-ink"
            >
              Go Premium
            </button>
          )}
          <button onClick={() => navigate("/search")} data-testid="nav-search-btn" title="Search" className="text-bone hover:text-gold">
            <Search className="h-5 w-5" />
          </button>
          {user ? (
            <>
              {user.is_admin && (
                <button onClick={() => navigate("/admin")} className="text-sm text-gold hover:underline" data-testid="nav-admin-btn">
                  Admin
                </button>
              )}
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2"
                data-testid="nav-dashboard-btn"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-gold/40" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-gold/40 bg-ash text-xs text-gold">
                    {user.name?.[0] || "U"}
                  </span>
                )}
                <span className="text-sm text-bone">Dashboard</span>
              </button>
              <button onClick={logout} className="text-muted hover:text-gold" data-testid="logout-btn" title="Sign out">
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <Button variant="outline" onClick={login} data-testid="login-btn" className="px-5 py-2">
              Sign In
            </Button>
          )}
        </div>

        <button className="lg:hidden text-bone" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-edge bg-ink px-6 py-4 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-bone hover:text-gold"
              >
                {l.label}
              </NavLink>
            ))}
            {!user?.is_premium && (
              <button
                onClick={() => { setOpen(false); navigate("/premium"); }}
                data-testid="mobile-premium-btn"
                className="mt-2 border border-gold bg-gold/10 px-3 py-2 text-sm text-gold"
              >
                Go Premium — $3/month
              </button>
            )}
            <div className="mt-3 border-t border-edge pt-3">
              {user ? (
                <div className="flex items-center justify-between">
                  <button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="text-gold">
                    Dashboard
                  </button>
                  <button onClick={logout} className="text-muted">Sign out</button>
                </div>
              ) : (
                <Button variant="outline" onClick={login} className="w-full">Sign In</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
